import withStyles from '@mui/styles/withStyles';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import JoinAudioButton from './JoinAudioButton';
import AudioSettingsButton from './AudioSettingsButton';
import TileViewButton from './TileViewButton';
import WhiteboardButton from './WhiteboardButton';
import SettingsButton from './SettingsButton';
import MoreSettingsButton from './MoreSettingsButton';
import MicrophoneButton from './MicrophoneButton';
import SpeakerButton from './SpeakerButton';
import withBoundary from 'component/ErrorBoundary/withBoundary';
import {
  getToolbarButtons,
  isToolbarButtonEnabled
} from 'store/modules/base/config';
import {
  NOTIFY_CLICK_MODE,
  NOT_APPLICABLE,
  THRESHOLDS
} from 'store/modules/toolbox/constants';
import {
  setHangupMenuVisible,
  setOverflowMenuVisible,
  setToolbarHovered,
  showToolbox
} from 'store/modules/toolbox/actions';
import { translate } from 'store/modules/base/i18n';
import { connect } from 'store/modules/base/redux';
import { getJwtDisabledButtons } from 'store/modules/toolbox/functions';
import { getRemoteParticipantCount } from 'store/modules/base/participants';

const styles = () => {
  return {
    contextMenu: {
      position: 'relative',
      right: 'auto',
      maxHeight: 'inherit',
      margin: 0,
      marginBottom: '8px'
    },

    hangupMenu: {
      position: 'relative',
      right: 'auto',
      display: 'flex',
      flexDirection: 'column',
      rowGap: '8px',
      margin: 0,
      padding: '16px',
      marginBottom: '8px'
    }
  };
};

/**
 * Implements the conference toolbox on React/Web.
 *
 * @augments Component
 */
class Toolbox extends Component {
  /**
   * Initializes a new {@code Toolbox} instance.
   *
   * @param {IProps} props - The read-only React {@code Component} props with
   * which the new instance is to be initialized.
   */
  constructor(props) {
    super(props);
    this._onEscKey = this._onEscKey.bind(this);
    this._onTabIn = this._onTabIn.bind(this);
  }

  /**
   * Sets keyboard shortcuts for to trigger ToolbarButtons actions.
   *
   * @inheritdoc
   * @returns {void}
   */
  componentDidMount() {}

  /**
   * Update the visibility of the {@code OverflowMenuButton}.
   *
   * @inheritdoc
   */
  componentDidUpdate(prevProps) {
    const { _dialog, _visible, dispatch } = this.props;

    if (prevProps._overflowMenuVisible && !prevProps._dialog && _dialog) {
      this._onSetOverflowVisible(false);
      dispatch(setToolbarHovered(false));
    }
    if (prevProps._hangupMenuVisible && prevProps._visible && !_visible) {
      this._onSetHangupVisible(false);
      dispatch(setToolbarHovered(false));
    }
  }

  /*
   * Removes keyboard shortcuts registered by this component.
   *
   * @inheritdoc
   * @returns {void}
   */
  componentWillUnmount() {}

  /**
   * Implements React's {@link Component#render()}.
   *
   * @inheritdoc
   * @returns {ReactElement}
   */
  render() {
    if (this.props._disabled) {
      return null;
    }

    const { _chatOpen, _visible, _toolbarButtons } = this.props;
    const rootClassNames = `new-toolbox ${_visible ? 'visible' : ''} ${
      _toolbarButtons.length ? '' : 'no-buttons'
    } ${_chatOpen ? 'shift-right' : ''}`;

    return (
      <div className={rootClassNames} id="new-toolbox">
        {this._renderToolboxContent()}
      </div>
    );
  }

  /**
   * Key handler for overflow/hangup menus.
   *
   * @param {KeyboardEvent} e - Esc key click to close the popup.
   * @returns {void}
   */
  _onEscKey(e) {
    if (e?.key === 'Escape') {
      e?.stopPropagation();
      this._closeHangupMenuIfOpen();
      this._closeOverflowMenuIfOpen();
    }
  }

  /**
   * Closes the hangup menu if opened.
   *
   * @private
   * @returns {void}
   */
  _closeHangupMenuIfOpen() {
    const { dispatch, _hangupMenuVisible } = this.props;

    _hangupMenuVisible && dispatch(setHangupMenuVisible(false));
  }

  /**
   * Closes the overflow menu if opened.
   *
   * @private
   * @returns {void}
   */
  _closeOverflowMenuIfOpen() {
    const { dispatch, _overflowMenuVisible } = this.props;

    _overflowMenuVisible && dispatch(setOverflowMenuVisible(false));
  }

  /**
   * Returns all buttons that could be rendered.
   *
   * @param {Object} state - The redux state.
   * @returns {Object} The button maps mainMenuButtons and overflowMenuButtons.
   */
  _getAllButtons() {
    const { _isVideoOnly, _clientWidth, _user, _devices } = this.props;

    const joinAudio =
      _user.device?.options?.showJoinLeaveAudioButton == 1
        ? {
            key: 'joinaudio',
            Content: JoinAudioButton,
            group: 2
          }
        : {
            key: 'joinaudio',
            Content: SpeakerButton,
            group: 2
          };
    const microphone =
      !_isVideoOnly &&
      (_user.device?.options?.showMicrophoneButton == 1
        ? {
            key: 'microphone',
            Content: AudioSettingsButton,
            group: 2
          }
        : {
            key: 'microphone',
            Content: MicrophoneButton,
            group: 2
          });

    const tileview = _clientWidth > 500 &&
      _user.device?.options?.showViewButton &&
      _devices > 1 && {
        key: 'tileview',
        Content: TileViewButton,
        group: 3
      };

    const whiteboard = _isVideoOnly &&
      _user.device?.options?.showWhiteboardButton && {
        key: 'whiteboard',
        Content: WhiteboardButton,
        group: 4
      };

    const settings = {
      key: 'settings',
      Content: SettingsButton,
      group: 5
    };

    const more = {
      key: 'more',
      Content: MoreSettingsButton,
      group: 4
    };
    return [joinAudio, microphone, whiteboard, tileview, more, settings];
  }

  /**
   * Returns the notify mode of the given toolbox button.
   *
   * @param {string} btnName - The toolbar button's name.
   * @returns {string|undefined} - The button's notify mode.
   */
  _getButtonNotifyMode(btnName) {
    const notify = this.props._buttonsWithNotifyClick?.find(
      (btn) =>
        (typeof btn === 'string' && btn === btnName) ||
        (typeof btn === 'object' && btn.key === btnName)
    );

    if (notify) {
      return typeof notify === 'string' || notify.preventExecution
        ? NOTIFY_CLICK_MODE.PREVENT_AND_NOTIFY
        : NOTIFY_CLICK_MODE.ONLY_NOTIFY;
    }
  }

  /**
   * Sets the notify click mode for the buttons.
   *
   * @param {Object} buttons - The list of toolbar buttons.
   * @returns {void}
   */
  _setButtonsNotifyClickMode(buttons) {
    if (
      typeof APP === 'undefined' ||
      !this.props._buttonsWithNotifyClick?.length
    ) {
      return;
    }

    Object.values(buttons).forEach((button) => {
      if (typeof button === 'object') {
        button.notifyMode = this._getButtonNotifyMode(button.key);
      }
    });
  }

  /**
   * Returns all buttons that need to be rendered.
   *
   * @param {Object} state - The redux state.
   * @returns {Object} The visible buttons arrays .
   */
  _getVisibleButtons() {
    const { _clientWidth, _toolbarButtons, _jwtDisabledButons } = this.props;

    const buttons = this._getAllButtons();

    this._setButtonsNotifyClickMode(buttons);
    const isHangupVisible = isToolbarButtonEnabled('hangup', _toolbarButtons);
    const { order } =
      THRESHOLDS.find(({ width }) => _clientWidth > width) ||
      THRESHOLDS[THRESHOLDS.length - 1];
    let sliceIndex = order.length + 2;

    const keys = Object.keys(buttons);
    const filtered = [
      ...Object.values(buttons).filter(
        (button, index) => !order.includes(keys[index]) && buttons[keys[index]]
      ),
      ...order.map((key) => buttons[key])
    ]
      .filter(Boolean)
      .filter(
        ({ key, alias = NOT_APPLICABLE }) =>
          !_jwtDisabledButons.includes(key) &&
          (isToolbarButtonEnabled(key, _toolbarButtons) ||
            isToolbarButtonEnabled(alias, _toolbarButtons))
      );

    if (isHangupVisible) {
      sliceIndex -= 1;
    }

    // This implies that the overflow button will be displayed, so save some space for it.
    if (sliceIndex < filtered.length) {
      sliceIndex -= 1;
    }
    return {
      mainMenuButtons: filtered.slice(0, sliceIndex),
      overflowMenuButtons: filtered.slice(sliceIndex)
    };
  }

  /**
   * Sets the visibility of the hangup menu.
   *
   * @param {boolean} visible - Whether or not the hangup menu should be
   * displayed.
   * @private
   * @returns {void}
   */
  _onSetHangupVisible(visible) {
    this.props.dispatch(setHangupMenuVisible(visible));
    this.props.dispatch(setToolbarHovered(visible));
  }

  /**
   * Sets the visibility of the overflow menu.
   *
   * @param {boolean} visible - Whether or not the overflow menu should be
   * displayed.
   * @private
   * @returns {void}
   */
  _onSetOverflowVisible(visible) {
    this.props.dispatch(setOverflowMenuVisible(visible));
    this.props.dispatch(setToolbarHovered(visible));
  }

  /**
   * Toggle the toolbar visibility when tabbing into it.
   *
   * @returns {void}
   */
  _onTabIn() {
    if (!this.props._visible) {
      this.props.dispatch(showToolbox());
    }
  }

  /**
   * Renders the toolbox content.
   *
   * @returns {ReactElement}
   */
  _renderToolboxContent() {
    const containerClassName = `toolbox-content`;

    const buttons = this._getAllButtons().filter((val) => Boolean(val));
    return (
      <div className={containerClassName}>
        <div className="toolbox-content-wrapper" onFocus={this._onTabIn}>
          <div className="toolbox-content-items">
            {buttons.map(({ Content, key, ...rest }) => (
              <Content {...rest} buttonKey={key} key={key} />
            ))}
          </div>
        </div>
      </div>
    );
  }
}

/**
 * Maps (parts of) the redux state to {@link Toolbox}'s React {@code Component}
 * props.
 *
 * @param {Object} state - The redux store/state.
 * @param {Object} ownProps - The props explicitly passed.
 * @private
 * @returns {{}}
 */
function _mapStateToProps(state, ownProps) {
  const { user } = state['features/login'];
  const { buttonsWithNotifyClick, iAmRecorder, iAmSipGateway } =
    state['features/base/config'];
  const { hangupMenuVisible, overflowMenuVisible } = state['features/toolbox'];
  const { clientWidth, clientHeight } = state['features/base/responsive-ui'];
  const toolbarButtons = ownProps.toolbarButtons || getToolbarButtons(state);

  return {
    _user: user,
    _devices: getRemoteParticipantCount(state),
    _buttonsWithNotifyClick: buttonsWithNotifyClick,
    _chatOpen: state['features/chat'].isOpen,
    _clientWidth: clientWidth,
    _dialog: Boolean(state['features/base/dialog'].component),
    _disabled: Boolean(iAmRecorder || iAmSipGateway),
    _isVideoOnly: state['features/settings'].videoOnly,
    _hangupMenuVisible: hangupMenuVisible,
    _jwtDisabledButons: getJwtDisabledButtons(state),
    _overflowMenuVisible: overflowMenuVisible,
    _toolbarButtons: toolbarButtons,
    _visible:
      (clientWidth < 500 || (clientWidth > 500 && clientHeight < 500)) &&
      ownProps.visible
  };
}
Toolbox.propTypes = {
  _user: PropTypes.object,
  _buttonsWithNotifyClick: PropTypes.func,
  _chatOpen: PropTypes.bool,
  _clientWidth: PropTypes.number,
  _dialog: PropTypes.bool,
  _disabled: PropTypes.bool,
  _isVideoOnly: PropTypes.bool,
  _hangupMenuVisible: PropTypes.bool,
  _overflowMenuVisible: PropTypes.bool,
  _jwtDisabledButons: PropTypes.array,
  _toolbarButtons: PropTypes.array,
  _visible: PropTypes.bool,
  _devices: PropTypes.any,
  dispatch: PropTypes.func
};
export default React.memo(
  translate(
    connect(_mapStateToProps)(withStyles(styles)(withBoundary(Toolbox)))
  )
);
